/* Copyright (c) 2020-present CloudIO Inc */

import { ActionItemType } from 'componentregistry/registry/Base.type';
import { Platform } from '../../platform/Platform';
import {
  ActionManager,
  ActionManagerFn,
  AliasType,
} from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionQueryChart';
import { analyzeActionQueryChart } from './ActionQueryChartAnalyze';

const ActionQueryChartManager: ActionManagerFn<'ActionQueryChart'> = (
  platform: Platform,
): ActionManager<'ActionQueryChart'> => {
  return {
    ...baseActionManager(platform, 'ActionQueryChart'),
    defaultValue,
    props: {
      ...baseActionProps,
      chartItemId: {
        label: 'Chart Item ID',
        type: 'ItemID',
        initialFilter: 'Chartjs',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (
            item?.type !== 'Chartjs' &&
            item?.type !== 'FusionChartsIO' &&
            item?.type !== 'AngularGauge' &&
            item?.type !== 'SparkLine'
          ) {
            return `Item id '${value}' doesn't belong to a Chart`;
          }
          return '';
        },
      },
      showEmptyFilterWarning: {
        label: 'Show Empty Filter Warning',
        type: 'boolean',
        helpText:
          'Enable this to know if empty value passed to filters(Recommended for developer).',
      },
      force: {
        label: 'Force',
        type: 'boolean',
        helpText:
          'Force the query to be executed even if the filters are not changed',
      },
    },
    isChildAllowed: (page, item, childType) =>
      (
        [
          'ActionSetFilterCombiner',
          'ActionSetQueryFilter',
          'ActionSetSimpleQueryFilter',
        ] as ActionItemType[]
      ).includes(childType as ActionItemType),
    getDataSourceAliasProperty: (aliasType: AliasType) => {
      return aliasType === 'QUERY_FILTER'
        ? { aliasProp: 'dsalias', embeddedPageIdProp: 'embeddedPageId' }
        : undefined;
    },
    getDisplayText: (item) => item.props.chartItemId,
    name: 'Query Chart',
    analyze: analyzeActionQueryChart,
    icon: { iconName: 'search' },
    description: `**Overview**<br/>
The Query Chart action is a data visualization control component designed to programmatically refresh or update the data displayed in a specific Chart component. It functions as a bridge between user inputs (like filter forms or date pickers) and the visual output, allowing developers to re-execute the chart's underlying database query with new parameters.<br/><br/>
This action serves as a wrapper for data fetching logic specifically tied to visualization widgets. It supports a suite of child components—ActionSetQueryFilter, ActionSetSimpleQueryFilter, and ActionSetFilterCombiner—that allow developers to construct complex, dynamic filtering criteria that are applied strictly to this query execution.

**Key Features**<br/>
* **Targeted Refresh:** Specific validation ensures this action only targets compatible visualization components (Chartjs, FusionChartsIO, AngularGauge, SparkLine), preventing configuration errors.
* **Ad-Hoc Filtering:** Acts as a container for filter-defining child actions, enabling the chart to display a subset of data (e.g., "Sales for 2023") without permanently altering the global data store state.
* **Cache Control:** Includes a force property to bypass standard optimization checks, compelling the system to re-fetch data even if the filter parameters appear unchanged (useful for real-time dashboards).
* **Developer Diagnostics:** Features a showEmptyFilterWarning flag to help debug scenarios where filters might be accidentally passing null or undefined values during development.

**Properties**<br/>
* **Chart Item ID (chartItemId) (Required):** A property of type ItemID.
  * *Function:* Identifies the specific Chart component to update.
  * *Validation:* Must reference a supported chart type (Chartjs, FusionCharts, Gauge, or SparkLine).
* **Show Empty Filter Warning:** A boolean property.
  * *Function:* If enabled, the system logs a warning if the query is executed with empty filter values, helping identify logic gaps in filter expressions.
* **Force:** A boolean property.
  * *Function:* If true, forces the query to execute regardless of the current state or caching logic.

**Use Cases**<br/>
* **Dashboard Filtering:** A user selects a date range and clicks "Apply"; this action triggers to reload the "Revenue" chart with data only from that specific period.
* **Live Monitoring:** Using a Timer event to trigger this action every 60 seconds with force: true to update a "Server Load" gauge with the latest metrics.
* **Contextual Updates:** Clicking a "Region" on a map component triggers this action to update a secondary "Sales by City" chart to show data relevant only to the selected region.`,
  };
};

export default ActionQueryChartManager;
